import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_PAGE_SIZE = { superadmin: 5, other: 20 };

// All read-only dashboard data: currencies, price list, claims, stats, balances, settings.
export default function useDashboardData({ token, axiosConfig, isSuperAdmin, canViewAllData, hospitalName, username, role }) {
  const [hospitalCurrency, setHospitalCurrency] = useState({ code: 'USD', symbol: '$', decimal_places: 2 });
  const [currencies, setCurrencies] = useState({});
  const [priceList, setPriceList] = useState([]);
  const [filteredPriceList, setFilteredPriceList] = useState([]);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [claims, setClaims] = useState([]);
  const [claimsTotal, setClaimsTotal] = useState(0);
  const [claimsTotalPages, setClaimsTotalPages] = useState(1);
  const [claimPage, setClaimPage] = useState(1);
  const [claimStatusFilter, setClaimStatusFilter] = useState('ALL');
  const [claimSearch, setClaimSearch] = useState('');
  const [claimPageSize, setClaimPageSize] = useState(isSuperAdmin ? DEFAULT_PAGE_SIZE.superadmin : DEFAULT_PAGE_SIZE.other);
  const [hospitalStats, setHospitalStats] = useState({});
  const [hospitalBalance, setHospitalBalance] = useState(0);
  const [totalClaims, setTotalClaims] = useState(0);
  const [allHospitals, setAllHospitals] = useState([]);
  const [systemStats, setSystemStats] = useState({ totalFamilies: 0, totalMembers: 0, totalClaims: 0, totalHospitals: 0 });
  const [todayStats, setTodayStats] = useState({ total: 0, count: 0 });
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [workflowEnabled, setWorkflowEnabled] = useState(false);
  const [preauthRequired, setPreauthRequired] = useState(false);

  const loadWorkflowStatus = async () => {
    if (canViewAllData) {
      setWorkflowEnabled(true);
      return;
    }
    try {
      const response = await axios.get(`${API}/hospital/workflow-status`, axiosConfig);
      setWorkflowEnabled(!!response.data.workflow_enabled);
      setPreauthRequired(!!response.data.preauth_required);
    } catch (error) {
      console.error('Failed to load workflow status:', error);
    }
  };

  const loadMaintenanceMode = async () => {
    try {
      const response = await axios.get(`${API}/settings/maintenance-mode`, axiosConfig);
      setMaintenanceMode(response.data.maintenance_mode || false);
    } catch (error) {
      console.error('Failed to load maintenance mode:', error);
    }
  };

  const toggleMaintenanceMode = async () => {
    try {
      const response = await axios.post(`${API}/settings/maintenance-mode`, {}, axiosConfig);
      setMaintenanceMode(response.data.maintenance_mode);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle maintenance mode');
    }
  };

  const loadTodayStats = async () => {
    if (!canViewAllData) return;
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API}/claims/daily-summary`, { ...axiosConfig, params: { date: today, limit: 100000 } });
      const dayClaims = res.data?.claims || [];
      const total = dayClaims.reduce((sum, c) => sum + Number(c.total_claim_amount || 0), 0);
      setTodayStats({ total, count: dayClaims.length });
    } catch (error) {
      console.error('Failed to load today stats:', error);
    }
  };

  const loadPriceList = async () => {
    try {
      const response = await axios.get(`${API}/pricelists`, axiosConfig);
      setPriceList(response.data);
      setFilteredPriceList(response.data);
    } catch (error) {
      toast.error('Failed to load price list');
    }
  };

  useEffect(() => {
    if (!itemSearchQuery.trim()) {
      setFilteredPriceList(priceList);
    } else {
      const query = itemSearchQuery.toLowerCase();
      setFilteredPriceList(priceList.filter(item =>
        item.item_id.toLowerCase().includes(query) ||
        item.item_name.toLowerCase().includes(query)
      ));
    }
  }, [itemSearchQuery, priceList]);

  const loadClaims = async (page = claimPage, statusFilter = claimStatusFilter, search = claimSearch, pageSize = claimPageSize) => {
    try {
      const endpoint = isSuperAdmin ? `${API}/admin/claims/all` : `${API}/claims`;
      const limit = pageSize;
      const skip = (page - 1) * limit;

      const params = { limit, skip };
      if (statusFilter && statusFilter !== 'ALL') {
        params.status = statusFilter;
      }
      if (search && search.trim()) {
        params.search = search.trim();
      }
      if (role === 'Finance' || role === 'Reception' || role === 'DataEntry') {
        params.current_month = true;
      }

      const response = await axios.get(endpoint, { ...axiosConfig, params });
      const data = response.data;

      // Legacy fallback: some endpoints previously returned a raw array
      if (Array.isArray(data)) {
        setClaims(data.slice(0, limit));
        setClaimsTotal(data.length);
        setClaimsTotalPages(Math.max(1, Math.ceil(data.length / limit)));
        return;
      }

      const claimsArr = data.claims || [];
      const total = typeof data.total === 'number' ? data.total : claimsArr.length;
      setClaims(claimsArr);
      setClaimsTotal(total);
      setClaimsTotalPages(Math.max(1, Math.ceil(total / limit)));
    } catch (error) {
      toast.error('Failed to load claims');
    }
  };

  useEffect(() => {
    if (!token) return;
    const t = setTimeout(() => loadClaims(claimPage, claimStatusFilter, claimSearch, claimPageSize), claimSearch ? 350 : 0);
    return () => clearTimeout(t);
  }, [claimPage, claimStatusFilter, claimSearch, claimPageSize]);

  const loadHospitalStats = async () => {
    try {
      const response = await axios.get(`${API}/claims/hospital-stats`, axiosConfig);
      setHospitalStats(response.data);
      if (!isSuperAdmin && hospitalName && response.data[hospitalName]) {
        setTotalClaims(response.data[hospitalName].total_pending);
      }
    } catch (error) {
      console.error('Failed to load hospital stats:', error);
    }
  };

  const loadHospitalBalance = async () => {
    try {
      const response = await axios.get(`${API}/hospital/balance`, axiosConfig);
      setHospitalBalance(response.data.deposit_balance);
    } catch (error) {
      console.error('Failed to load hospital balance:', error);
    }
  };

  const loadAllHospitals = async () => {
    if (!canViewAllData) return;
    try {
      const response = await axios.get(`${API}/admin/hospitals`, axiosConfig);
      setAllHospitals(response.data || []);
    } catch (error) {
      console.error('Failed to load all hospitals:', error);
    }
  };

  const loadSystemStats = async () => {
    try {
      const [familiesCountRes, membersCountRes, claimsRes, hospitalsRes] = await Promise.all([
        axios.get(`${API}/admin/families/count`, axiosConfig),
        axios.get(`${API}/admin/members/count`, axiosConfig),
        axios.get(`${API}/admin/claims/all`, axiosConfig),
        axios.get(`${API}/admin/hospitals`, axiosConfig)
      ]);
      const claimsData = claimsRes.data.data || claimsRes.data;
      const claimsCount = claimsRes.data.total || claimsData.length;
      setSystemStats({
        totalFamilies: familiesCountRes.data.count || 0,
        totalMembers: membersCountRes.data.count || 0,
        totalClaims: claimsCount,
        totalHospitals: hospitalsRes.data.length
      });
    } catch (error) {
      console.error('Failed to load system statistics:', error);
    }
  };

  const loadCurrencyInfo = async () => {
    try {
      const currenciesResponse = await axios.get(`${API}/currencies`, axiosConfig);
      const currencyMap = {};
      currenciesResponse.data.forEach(c => { currencyMap[c.code] = c; });
      setCurrencies(currencyMap);

      if (!isSuperAdmin && hospitalName) {
        try {
          const hospitalResponse = await axios.get(`${API}/hospital/info`, axiosConfig);
          if (hospitalResponse.data && hospitalResponse.data.currency_code) {
            const currency = currencyMap[hospitalResponse.data.currency_code] || { code: 'USD', symbol: '$', decimal_places: 2 };
            setHospitalCurrency(currency);
          }
        } catch (err) {
          console.log('Could not load hospital currency, defaulting to USD');
        }
      }
    } catch (error) {
      console.error('Failed to load currency info:', error);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadCurrencyInfo();
    loadPriceList();
    loadClaims();
    loadHospitalBalance();
    loadHospitalStats();
    loadWorkflowStatus();
    if (canViewAllData) {
      loadSystemStats();
      loadTodayStats();
      loadAllHospitals();
    }
    if (username === 'superadmin') {
      loadMaintenanceMode();
    }
  }, []);

  // ==================== CURRENCY HELPERS ====================

  const getFamilyBalanceInHospitalCurrency = (balanceUSD) => balanceUSD * (hospitalCurrency.rate_to_usd || 1);
  const getClaimAmountInHospitalCurrency = (amountUSD) => amountUSD * (hospitalCurrency.rate_to_usd || 1);

  const formatCurrency = (amount, currencyCode = null) => {
    const currency = currencyCode ? currencies[currencyCode] : hospitalCurrency;
    const symbol = currency?.symbol || '$';
    const decimals = currency?.decimal_places !== undefined ? currency.decimal_places : 2;
    const formattedAmount = amount.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    return `${symbol} ${formattedAmount}`;
  };

  return {
    hospitalCurrency, currencies,
    priceList, filteredPriceList, itemSearchQuery, setItemSearchQuery,
    claims, claimsTotal, claimsTotalPages, claimPage, setClaimPage, claimStatusFilter, setClaimStatusFilter, claimSearch, setClaimSearch, claimPageSize, setClaimPageSize,
    hospitalStats, hospitalBalance, totalClaims, allHospitals, systemStats, todayStats,
    maintenanceMode, toggleMaintenanceMode, workflowEnabled, preauthRequired,
    loadClaims, loadHospitalStats, loadHospitalBalance,
    formatCurrency, getFamilyBalanceInHospitalCurrency, getClaimAmountInHospitalCurrency,
  };
}
